<template>
  <div class="weekly-view-takeoff-boq w-full max-w-full min-w-0">
    <div class="weekly-view-takeoff-boq__toolbar">
      <div class="weekly-view-takeoff-boq__toolbar-row">
        <div class="weekly-view-takeoff-boq__field">
          <label class="weekly-view-takeoff-boq__label">
            {{ $t('hh_per_person_month') }}
            <span v-if="hhConfigRequired" class="weekly-view-takeoff-boq__required">*</span>
          </label>
          <div class="weekly-view-takeoff-boq__hh-edit">
            <b-input
              v-model.number="toolbar.hhPerPersonMonth"
              type="number"
              min="1"
              size="is-small"
              class="weekly-view-takeoff-boq__input weekly-view-takeoff-boq__input--narrow"
              :class="{ 'weekly-view-takeoff-boq__input--invalid': hhConfigInvalid }"
            />
            <span
              v-if="hhPerPersonMonthDirty"
              class="weekly-view-takeoff-boq__hh-actions"
            >
              <button
                type="button"
                class="weekly-view-takeoff-boq__hh-btn weekly-view-takeoff-boq__hh-btn--save"
                :disabled="savingHhConfig"
                :title="$t('save')"
                @click="confirmHhPerPersonMonth"
              >
                <b-icon pack="fas" icon="check" size="is-small" />
              </button>
              <button
                type="button"
                class="weekly-view-takeoff-boq__hh-btn weekly-view-takeoff-boq__hh-btn--cancel"
                :disabled="savingHhConfig || !canCancelHhPerPersonMonth"
                :title="$t('cancel')"
                @click="cancelHhPerPersonMonth"
              >
                <b-icon pack="fas" icon="times" size="is-small" />
              </button>
            </span>
          </div>
          <span
            v-if="hhConfigInvalid"
            class="weekly-view-takeoff-boq__hint weekly-view-takeoff-boq__hint--error"
          >
            {{ $t('field_required') }}
          </span>
          <span v-else class="weekly-view-takeoff-boq__hint">
            {{ $t('hh_per_person_month_hint') }}
          </span>
        </div>

        <span class="weekly-view-takeoff-boq__divider" aria-hidden="true" />

        <div class="weekly-view-takeoff-boq__field">
          <label class="weekly-view-takeoff-boq__label">{{ $t('start_month') }}</label>
          <b-input
            v-model="toolbar.startMonth"
            type="month"
            size="is-small"
            class="weekly-view-takeoff-boq__input weekly-view-takeoff-boq__input--month"
          />
        </div>

        <div class="weekly-view-takeoff-boq__field">
          <label class="weekly-view-takeoff-boq__label">{{ $t('months_until_project_end') }}</label>
          <b-input
            v-model.number="toolbar.monthsUntilEnd"
            type="number"
            min="1"
            size="is-small"
            class="weekly-view-takeoff-boq__input weekly-view-takeoff-boq__input--narrow"
          />
        </div>
      </div>

      <div class="weekly-view-takeoff-boq__toolbar-row weekly-view-takeoff-boq__toolbar-row--blocks">
        <span class="weekly-view-takeoff-boq__label">{{ $t('monthly_blocks') }}:</span>
        <b-checkbox
          v-model="toolbar.blocks.percentHh"
          size="is-small"
          class="weekly-view-takeoff-boq__block-check weekly-view-takeoff-boq__block-check--percent"
        >
          % / HH
        </b-checkbox>
        <b-checkbox
          v-model="toolbar.blocks.people"
          size="is-small"
          class="weekly-view-takeoff-boq__block-check weekly-view-takeoff-boq__block-check--people"
        >
          {{ $t('people') }}
        </b-checkbox>
        <b-checkbox
          v-model="toolbar.blocks.units"
          size="is-small"
          class="weekly-view-takeoff-boq__block-check weekly-view-takeoff-boq__block-check--units"
        >
          {{ $t('units') }}
        </b-checkbox>
      </div>
    </div>

    <div class="overflow-x-auto bg-white border border-gray-300 rounded">
      <table class="w-full min-w-max text-xs border-collapse">
        <thead>
          <tr class="weekly-view-takeoff-boq__group-row">
            <th
              v-for="group in columnGroups"
              :key="group.key"
              :colspan="group.columns.length"
              class="weekly-view-takeoff-boq__th weekly-view-takeoff-boq__th--group"
              :class="group.headerClass"
            >
              {{ $t(group.labelKey) }}
            </th>
          </tr>
          <tr class="weekly-view-takeoff-boq__column-row">
            <th
              v-for="column in flatColumns"
              :key="column.key"
              class="weekly-view-takeoff-boq__th weekly-view-takeoff-boq__th--column"
              :class="[
                column.headerClass,
                column.key === 'discipline_stage' ? 'weekly-view-takeoff-boq__th--name' : '',
              ]"
            >
              {{ column.label != null ? column.label : $t(column.labelKey) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td
              :colspan="Math.max(flatColumns.length, 1)"
              class="weekly-view-takeoff-boq__empty-state"
            >
              {{ $t('loading') }}...
            </td>
          </tr>
          <tr v-else-if="!roots.length">
            <td
              :colspan="Math.max(flatColumns.length, 1)"
              class="weekly-view-takeoff-boq__empty-state"
            >
              {{ $t('no_results') }}
            </td>
          </tr>
          <weekly-takeoff-boq-tree-rows
            v-else
            :project-id="projectId"
            :nodes="roots"
            :depth="0"
            :expanded-ids="expandedIds"
            :column-count="flatColumns.length"
            :stats-keys="statsKeys"
            :map-stats-keys="allStatsKeys"
            :with-takeoff-stats="true"
            :hh-per-person-month="toolbar.hhPerPersonMonth"
            @toggle="toggleExpand"
          />
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import WeeklyTakeoffBoqTreeRows from './weekly-takeoff-boq-tree-rows.vue'
import { mergeProjectConfigs } from './merge-project-configs'
import {
  applyTakeoffMonthlyStatsFromApi,
  takeoffMonthlyDerivedSnapshot,
} from './takeoff-monthly-stats'

const ROW_KIND_DISCIPLINE = 1
const ROW_KIND_STAGE = 2

const QUANTITY_DECIMALS = 5

function roundQuantity(value) {
  const factor = 10 ** QUANTITY_DECIMALS
  return Math.round(Number(value) * factor) / factor
}

const PROGRAMMING_COLUMNS = [
  { key: 'discipline_stage', labelKey: 'discipline_stage', group: 'programming' },
  { key: 'unit', labelKey: 'unit_abbr', group: 'programming' },
  { key: 'total_scope', labelKey: 'total_scope', group: 'programming' },
  { key: 'executed_accum', labelKey: 'executed_accum', group: 'programming' },
  { key: 'balance', labelKey: 'balance', group: 'programming' },
  { key: 'adjusted_man_hour_index', labelKey: 'adjusted_man_hour_index', group: 'programming' },
  { key: 'scheduled_man_hours', labelKey: 'scheduled_mh', group: 'programming' },
  { key: 'hh_to_finish', labelKey: 'hh_to_finish', group: 'programming' },
  { key: 'people_needed', labelKey: 'people_needed', group: 'programming' },
]

function currentYearMonth() {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  return `${now.getFullYear()}-${month}`
}

export default {
  name: 'weekly-view-takeoff-boq',

  components: {
    WeeklyTakeoffBoqTreeRows,
  },

  props: {
    projectId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      loading: true,
      roots: [],
      expandedIds: [],
      hhConfigHydrated: false,
      savingHhConfig: false,
      hhConfigTouched: false,
      toolbar: {
        hhPerPersonMonth: null,
        startMonth: currentYearMonth(),
        monthsUntilEnd: 8,
        blocks: {
          percentHh: true,
          people: true,
          units: true,
        },
      },
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    /** Sin valor guardado en configs: obligatorio configurar. */
    hhConfigRequired() {
      return this.savedHhPerPersonMonth() == null
    },

    hhPerPersonMonthDirty() {
      if (!this.hhConfigHydrated) {
        return false
      }
      const draft = this.normalizeHhValue(this.toolbar.hhPerPersonMonth)
      const saved = this.savedHhPerPersonMonth()
      if (draft == null && saved == null) {
        return this.hhConfigRequired
      }
      if (draft == null || saved == null) {
        return true
      }
      return Math.abs(draft - saved) >= 0.0001
    },

    canCancelHhPerPersonMonth() {
      return this.savedHhPerPersonMonth() != null
    },

    hhConfigInvalid() {
      if (!this.hhConfigHydrated) {
        return false
      }
      const draft = this.normalizeHhValue(this.toolbar.hhPerPersonMonth)
      if (this.hhConfigRequired && draft == null) {
        return true
      }
      return this.hhConfigTouched && draft == null
    },

    monthLocale() {
      const locale = (this.$i18n && this.$i18n.locale) ? this.$i18n.locale : 'es'
      if (locale === 'en') return 'en'
      if (locale === 'pt') return 'pt'
      if (locale === 'it') return 'it'
      return 'es'
    },

    monthPeriods() {
      const monthsCount = Math.max(1, Number(this.toolbar.monthsUntilEnd) || 1)
      const start = this.toolbar.startMonth || currentYearMonth()
      const [yearPart, monthPart] = start.split('-').map(Number)
      const startYear = yearPart || new Date().getFullYear()
      const startMonthIndex = (monthPart || 1) - 1
      const periods = []

      for (let offset = 0; offset < monthsCount; offset += 1) {
        const date = new Date(startYear, startMonthIndex + offset, 1)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const shortMonth = date
          .toLocaleDateString(this.monthLocale, { month: 'short' })
          .replace('.', '')
          .trim()
        const shortYear = String(year).slice(-2)

        periods.push({
          yearMonth: `${year}-${month}`,
          label: `${shortMonth}-${shortYear}`,
        })
      }

      return periods
    },

    monthlyHhDistributionColumns() {
      const columns = [
        {
          key: 'sigma_percent',
          label: 'Σ %',
          group: 'monthly_hh_distribution',
          headerClass: 'weekly-view-takeoff-boq__th--monthly',
        },
      ]

      this.monthPeriods.forEach((period) => {
        columns.push({
          key: `hh_pct_month_${period.yearMonth}`,
          label: period.label,
          group: 'monthly_hh_distribution',
          headerClass: 'weekly-view-takeoff-boq__th--monthly',
        })
      })

      return columns
    },

    peopleNeededPerMonthColumns() {
      return this.monthPeriods.map((period) => ({
        key: `people_month_${period.yearMonth}`,
        label: period.label,
        group: 'people_needed_per_month',
        headerClass: 'weekly-view-takeoff-boq__th--people-month',
      }))
    },

    remainingUnitsPerMonthColumns() {
      return this.monthPeriods.map((period) => ({
        key: `units_month_${period.yearMonth}`,
        label: period.label,
        group: 'remaining_units_per_month',
        headerClass: 'weekly-view-takeoff-boq__th--units-month',
      }))
    },

    allColumnGroups() {
      return [
        {
          key: 'programming',
          labelKey: 'programming',
          headerClass: 'weekly-view-takeoff-boq__group--programming',
          columns: PROGRAMMING_COLUMNS,
        },
        {
          key: 'monthly_hh_distribution',
          labelKey: 'monthly_remaining_hh_distribution',
          headerClass: 'weekly-view-takeoff-boq__group--monthly-hh',
          columns: this.monthlyHhDistributionColumns,
        },
        {
          key: 'people_needed_per_month',
          labelKey: 'people_needed_per_month',
          headerClass: 'weekly-view-takeoff-boq__group--people-month',
          columns: this.peopleNeededPerMonthColumns,
        },
        {
          key: 'remaining_units_per_month',
          labelKey: 'remaining_units_per_month',
          headerClass: 'weekly-view-takeoff-boq__group--units-month',
          columns: this.remainingUnitsPerMonthColumns,
        },
      ]
    },

    columnGroups() {
      return this.allColumnGroups.filter((group) => {
        if (group.key === 'programming') {
          return true
        }
        if (group.key === 'monthly_hh_distribution') {
          return this.toolbar.blocks.percentHh
        }
        if (group.key === 'people_needed_per_month') {
          return this.toolbar.blocks.people
        }
        if (group.key === 'remaining_units_per_month') {
          return this.toolbar.blocks.units
        }
        return true
      })
    },

    flatColumns() {
      return this.columnGroups.reduce((columns, group) => columns.concat(group.columns), [])
    },

    statsKeys() {
      return this.flatColumns
        .map((column) => column.key)
        .filter((key) => key !== 'discipline_stage')
    },

    allStatsKeys() {
      return this.allColumnGroups
        .reduce((columns, group) => columns.concat(group.columns), [])
        .map((column) => column.key)
        .filter((key) => key !== 'discipline_stage')
    },
  },
  watch: {
    project: {
      immediate: true,
      handler(project) {
        this.hydrateHhPerPersonMonth(project)
      },
    },
    projectId: {
      immediate: true,
      handler(value) {
        if (value) {
          this.loadRoots()
        } else {
          this.roots = []
        }
      },
    },
  },

  methods: {
    normalizeHhValue(raw) {
      if (raw == null || raw === '') {
        return null
      }
      const value = Number(raw)
      if (!Number.isFinite(value) || value <= 0) {
        return null
      }
      return value
    },

    savedHhPerPersonMonth() {
      const raw = this.project && this.project.configs
        ? this.project.configs.man_hour_per_person_month
        : null
      return this.normalizeHhValue(raw)
    },

    hydrateHhPerPersonMonth(project) {
      this.hhConfigHydrated = false
      this.hhConfigTouched = false
      const raw = project && project.configs
        ? project.configs.man_hour_per_person_month
        : null
      this.toolbar.hhPerPersonMonth = this.normalizeHhValue(raw)
      this.$nextTick(() => {
        this.hhConfigHydrated = true
      })
    },

    revertHhPerPersonMonth() {
      this.hhConfigHydrated = false
      this.hhConfigTouched = false
      this.toolbar.hhPerPersonMonth = this.savedHhPerPersonMonth()
      this.$nextTick(() => {
        this.hhConfigHydrated = true
      })
    },

    confirmHhPerPersonMonth() {
      this.hhConfigTouched = true
      const value = this.normalizeHhValue(this.toolbar.hhPerPersonMonth)
      if (value == null) {
        this.$notify.error('field_required')
        return
      }

      const current = this.savedHhPerPersonMonth()
      if (current != null && Math.abs(current - value) < 0.0001) {
        return
      }

      this.saveHhPerPersonMonth(value)
    },

    cancelHhPerPersonMonth() {
      if (!this.canCancelHhPerPersonMonth) {
        return
      }
      this.revertHhPerPersonMonth()
    },

    async saveHhPerPersonMonth(value) {
      if (!this.projectId || this.savingHhConfig) {
        return
      }

      this.savingHhConfig = true
      try {
        await mergeProjectConfigs(
          this.projectId,
          { man_hour_per_person_month: value },
          { store: this.$store },
        )
        this.hhConfigTouched = false
        this.toolbar.hhPerPersonMonth = value
        this.expandedIds = []
        await this.loadRoots()
      } catch (error) {
        this.$notify.error('error_saving')
        this.revertHhPerPersonMonth()
      } finally {
        this.savingHhConfig = false
      }
    },

    emptyMonthlyStats() {
      const stats = {}
      this.allColumnGroups.forEach((group) => {
        if (group.key === 'programming') {
          return
        }
        group.columns.forEach((column) => {
          stats[column.key] = 0
        })
      })
      return stats
    },

    mapNode(raw) {
      const rowKind = raw.row_kind != null ? Number(raw.row_kind) : null
      const takeoffStats = {
        unit: raw.unit ?? null,
        total_scope: raw.total_scope ?? null,
        executed_accum: raw.executed_accum ?? null,
        balance: raw.balance ?? null,
        adjusted_man_hour_index: raw.adjusted_man_hour_index ?? null,
        scheduled_man_hours: raw.scheduled_man_hours ?? null,
        hh_to_finish: raw.hh_to_finish ?? null,
        people_needed: raw.people_needed ?? null,
        ...this.emptyMonthlyStats(),
      }
      applyTakeoffMonthlyStatsFromApi(takeoffStats, raw, {
        mappingStatsKeys: this.allStatsKeys,
        hhPctKeys: this.allStatsKeys.filter((key) => String(key).startsWith('hh_pct_month_')),
      })

      return {
        id: raw.id,
        code: raw.code || '',
        name: raw.name || '',
        rowKind,
        childrenCount: Number(raw.children_count) || 0,
        takeoffStats,
        savedHhPct: this.snapshotHhPct(takeoffStats),
        savedMonthlyDerived: takeoffMonthlyDerivedSnapshot(raw),
        savingHhPct: false,
        badgeLabel: rowKind === ROW_KIND_STAGE
          ? 'STAGE'
          : (rowKind === ROW_KIND_DISCIPLINE ? 'DISC' : (raw.code || '')),
        _raw: raw,
      }
    },

    hhPctMonthKeysFromStats(stats) {
      return Object.keys(stats)
        .filter((key) => String(key).startsWith('hh_pct_month_'))
        .reduce((sum, key) => {
          const value = Number(stats[key])
          return sum + (Number.isFinite(value) ? value : 0)
        }, 0)
    },

    snapshotHhPct(stats) {
      const snapshot = {}
      Object.keys(stats).forEach((key) => {
        if (String(key).startsWith('hh_pct_month_')) {
          snapshot[key] = Number(stats[key]) || 0
        }
      })
      return snapshot
    },

    async loadRoots() {
      if (!this.projectId) {
        this.roots = []
        return
      }

      this.loading = true
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/boq-roots`,
          {
            params: {
              only_takeoff: 1,
              with_takeoff_stats: 1,
            },
          }
        )
        const list = (data && data.success && data.boq_roots) ? data.boq_roots : []
        this.roots = list.map((node) => this.mapNode(node))
        this.expandedIds = []
      } catch (error) {
        this.roots = []
        this.$notify.error('error_loading_data')
      } finally {
        this.loading = false
      }
    },

    toggleExpand(nodeId) {
      if (this.expandedIds.includes(nodeId)) {
        this.expandedIds = this.expandedIds.filter((id) => id !== nodeId)
        return
      }
      this.expandedIds = [...this.expandedIds, nodeId]
    },
  },
}
</script>

<style scoped>
.weekly-view-takeoff-boq__toolbar {
  margin-bottom: 0.75rem;
  padding: 0.75rem 1rem;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}

.weekly-view-takeoff-boq__toolbar-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  align-items: center;
}

.weekly-view-takeoff-boq__toolbar-row--blocks {
  margin-top: 0.65rem;
  padding-top: 0.65rem;
  border-top: 1px solid #e5e7eb;
}

.weekly-view-takeoff-boq__field {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.4rem 0.5rem;
  align-items: center;
}

.weekly-view-takeoff-boq__label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}

.weekly-view-takeoff-boq__hint {
  font-size: 0.7rem;
  color: #6b7280;
}

.weekly-view-takeoff-boq__hint--error {
  color: #b91c1c;
  font-weight: 600;
}

.weekly-view-takeoff-boq__required {
  color: #b91c1c;
  margin-left: 0.125rem;
}

.weekly-view-takeoff-boq__hh-edit {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.weekly-view-takeoff-boq__hh-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
}

.weekly-view-takeoff-boq__hh-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  background: #fff;
  cursor: pointer;
}

.weekly-view-takeoff-boq__hh-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.weekly-view-takeoff-boq__hh-btn--save {
  border-color: #86efac;
  color: #15803d;
}

.weekly-view-takeoff-boq__hh-btn--save:hover:not(:disabled) {
  background: #dcfce7;
}

.weekly-view-takeoff-boq__hh-btn--cancel {
  border-color: #fca5a5;
  color: #b91c1c;
}

.weekly-view-takeoff-boq__hh-btn--cancel:hover:not(:disabled) {
  background: #fee2e2;
}

.weekly-view-takeoff-boq__divider {
  display: inline-block;
  width: 1px;
  height: 1.75rem;
  background: #d1d5db;
}

.weekly-view-takeoff-boq__input {
  margin-bottom: 0;
}

.weekly-view-takeoff-boq__input--narrow {
  width: 5.5rem;
}

.weekly-view-takeoff-boq__input--invalid :deep(input),
.weekly-view-takeoff-boq__input--invalid :deep(.input) {
  border-color: #ef4444;
}

.weekly-view-takeoff-boq__input--month {
  width: 8.5rem;
}

.weekly-view-takeoff-boq__input--month :deep(input) {
  color: #0f766e;
  font-weight: 600;
}

.weekly-view-takeoff-boq__block-check {
  margin-right: 0.75rem;
}

.weekly-view-takeoff-boq__block-check--percent :deep(.control-label) {
  color: #6d28d9;
  font-weight: 600;
}

.weekly-view-takeoff-boq__block-check--people :deep(.control-label) {
  color: #2563eb;
  font-weight: 600;
}

.weekly-view-takeoff-boq__block-check--units :deep(.control-label) {
  color: #b45309;
  font-weight: 600;
}

.weekly-view-takeoff-boq__th {
  padding: 0.5rem 0.75rem;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  border: 1px solid #0d9488;
  white-space: nowrap;
}

.weekly-view-takeoff-boq__th--group {
  text-align: center;
  background-color: #0f766e;
}

.weekly-view-takeoff-boq__th--column {
  text-align: center;
  background-color: #0d9488;
  font-weight: 600;
}

.weekly-view-takeoff-boq__group--programming {
  background-color: #0f766e;
}

.weekly-view-takeoff-boq__group--monthly-hh {
  background-color: #6d28d9;
}

.weekly-view-takeoff-boq__th--monthly {
  background-color: #7c3aed;
  border-color: #6d28d9;
}

.weekly-view-takeoff-boq__group--people-month {
  background-color: #1d4ed8;
}

.weekly-view-takeoff-boq__th--people-month {
  background-color: #2563eb;
  border-color: #1d4ed8;
}

.weekly-view-takeoff-boq__group--units-month {
  background-color: #a16207;
}

.weekly-view-takeoff-boq__th--units-month {
  background-color: #ca8a04;
  border-color: #a16207;
}

.weekly-view-takeoff-boq__th--name {
  text-align: left;
  min-width: 16rem;
}

.weekly-view-takeoff-boq__empty-state {
  padding: 2rem 1rem;
  text-align: center;
  font-size: 0.875rem;
  color: #6b7280;
  background-color: #fff;
}
</style>
